import Vue from 'vue'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_axios_cfbb3946 from 'nuxt_plugin_axios_cfbb3946' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_axios_3566aa80 from 'nuxt_plugin_axios_3566aa80' // Source: ../plugins/axios (mode: 'client')
import nuxt_plugin_axiosSsr_5658b99c from 'nuxt_plugin_axiosSsr_5658b99c' // Source: ../plugins/axiosSsr (mode: 'server')
import nuxt_plugin_vuelazyload_211b2937 from 'nuxt_plugin_vuelazyload_211b2937' // Source: ../plugins/vue-lazyload (mode: 'client')
import nuxt_plugin_vueinfinitescroll_18d3fb29 from 'nuxt_plugin_vueinfinitescroll_18d3fb29' // Source: ../plugins/vue-infinite-scroll (mode: 'client')
import nuxt_plugin_common_7195563e from 'nuxt_plugin_common_7195563e' // Source: ../plugins/common (mode: 'client')
import nuxt_plugin_util_6a876078 from 'nuxt_plugin_util_6a876078' // Source: ../plugins/util (mode: 'all')
import nuxt_plugin_vuetoasted_f6148aee from 'nuxt_plugin_vuetoasted_f6148aee' // Source: ../plugins/vue-toasted (mode: 'client')
import nuxt_plugin_vuejsmodal_f50827f4 from 'nuxt_plugin_vuejsmodal_f50827f4' // Source: ../plugins/vue-js-modal (mode: 'all')
import nuxt_plugin_route_338f5eda from 'nuxt_plugin_route_338f5eda' // Source: ../plugins/route (mode: 'client')
import nuxt_plugin_sentry_3c12fa56 from 'nuxt_plugin_sentry_3c12fa56' // Source: ../plugins/sentry (mode: 'client')
import nuxt_plugin_vueqr_3319a71e from 'nuxt_plugin_vueqr_3319a71e' // Source: ../plugins/vueqr (mode: 'client')
import nuxt_plugin_haboconfig_d334ca9a from 'nuxt_plugin_haboconfig_d334ca9a' // Source: ../plugins/habo-config (mode: 'client')
import nuxt_plugin_icons_34981d38 from 'nuxt_plugin_icons_34981d38' // Source: ../plugins/icons (mode: 'all')
import nuxt_plugin_usereactcomponent_6a961a9a from 'nuxt_plugin_usereactcomponent_6a961a9a' // Source: ../plugins/use-react-component (mode: 'client')
import nuxt_plugin_elementui_a6a1b20a from 'nuxt_plugin_elementui_a6a1b20a' // Source: ../plugins/element-ui (mode: 'client')
import nuxt_plugin_refer_3398ea6c from 'nuxt_plugin_refer_3398ea6c' // Source: ../plugins/refer (mode: 'client')
import nuxt_plugin_sourceToLeads_aa747aea from 'nuxt_plugin_sourceToLeads_aa747aea' // Source: ../plugins/sourceToLeads (mode: 'client')
import nuxt_plugin_clearLocalStorage_367d1ae4 from 'nuxt_plugin_clearLocalStorage_367d1ae4' // Source: ../base/plugins/clearLocalStorage (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

async function createApp (ssrContext) {
  const router = await createRouter(ssrContext)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  const registerModule = store.registerModule
  store.registerModule = (path, rawModule, options) => registerModule.call(store, path, rawModule, Object.assign({ preserveState: process.client }, options))

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        const nuxt = this.nuxt || this.$options.nuxt
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  const inject = function (key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error('inject(key, value) has no value provided')
    }

    key = '$' + key
    // Add into app
    app[key] = value

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Plugin execution

  if (typeof nuxt_plugin_axios_cfbb3946 === 'function') {
    await nuxt_plugin_axios_cfbb3946(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_axios_3566aa80 === 'function') {
    await nuxt_plugin_axios_3566aa80(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_axiosSsr_5658b99c === 'function') {
    await nuxt_plugin_axiosSsr_5658b99c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuelazyload_211b2937 === 'function') {
    await nuxt_plugin_vuelazyload_211b2937(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueinfinitescroll_18d3fb29 === 'function') {
    await nuxt_plugin_vueinfinitescroll_18d3fb29(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_common_7195563e === 'function') {
    await nuxt_plugin_common_7195563e(app.context, inject)
  }

  if (typeof nuxt_plugin_util_6a876078 === 'function') {
    await nuxt_plugin_util_6a876078(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuetoasted_f6148aee === 'function') {
    await nuxt_plugin_vuetoasted_f6148aee(app.context, inject)
  }

  if (typeof nuxt_plugin_vuejsmodal_f50827f4 === 'function') {
    await nuxt_plugin_vuejsmodal_f50827f4(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_route_338f5eda === 'function') {
    await nuxt_plugin_route_338f5eda(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentry_3c12fa56 === 'function') {
    await nuxt_plugin_sentry_3c12fa56(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueqr_3319a71e === 'function') {
    await nuxt_plugin_vueqr_3319a71e(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_haboconfig_d334ca9a === 'function') {
    await nuxt_plugin_haboconfig_d334ca9a(app.context, inject)
  }

  if (typeof nuxt_plugin_icons_34981d38 === 'function') {
    await nuxt_plugin_icons_34981d38(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_usereactcomponent_6a961a9a === 'function') {
    await nuxt_plugin_usereactcomponent_6a961a9a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_elementui_a6a1b20a === 'function') {
    await nuxt_plugin_elementui_a6a1b20a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_refer_3398ea6c === 'function') {
    await nuxt_plugin_refer_3398ea6c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sourceToLeads_aa747aea === 'function') {
    await nuxt_plugin_sourceToLeads_aa747aea(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_clearLocalStorage_367d1ae4 === 'function') {
    await nuxt_plugin_clearLocalStorage_367d1ae4(app.context, inject)
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, () => {
        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from, next) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
